// the functionality is hidden until the implementation of the super admin role
import React, { useState, useCallback, useRef, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// import { ReactComponent as ChangeCompanyIcon } from "../../assets/changeCompany.svg";
import { ReactComponent as AboutIcon } from "../../assets/about.svg";
// import { ReactComponent as DjangoAdminIcon } from "../../assets/djangoAdmin.svg";
import { ReactComponent as LogOutIcon } from "../../assets/logOut.svg";
import {
  toggleModal,
  USER_MODAL,
  VERSION_MODAL,
  // CHANGE_COMPANY_MODAL,
} from "Slices/modals";
import { getCurrentUserName, getIsAdmin } from "Slices/currentUser";
import { useOnClickOutside } from "Utils/hooks";
import { UserAvatarComponent } from "../../../common/UserAvatarComponent";
import { clearTelemetryUser } from "Utils/faroUtils";
import { appTokenStore } from "Utils/authTokenStore";
import { useAuth } from "Components/Authentication/AuthProvider";
import { useHasAdminAccess } from "Components/Authentication/useRoleAccess";

import {
  UserInfoStyled,
  UserNameStyled,
  UserImageStyled,
  DropdownContentStyled,
  DropdownItemStyled,
  UserInfoIconStyled,
} from "./styles";

interface DropdownConfigStyles {
  id: string;
  text: string;
  clickAction: () => void;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  onlyAdmin: boolean;
}

export const UserInfo = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userName = useSelector(getCurrentUserName);

  // The session's canonical role (ID token `cognito:groups`) decides; the legacy
  // `User.role` selector still counts while RBAC runs in dual mode.
  const isSessionAdmin = useHasAdminAccess();
  const isLegacyAdmin = useSelector(getIsAdmin);
  const isAdmin = isSessionAdmin || isLegacyAdmin;
  const { logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
  const handleClickOutside = useCallback(() => setShowDropdown(false), []);
  useOnClickOutside(ref, handleClickOutside);

  const handleLogout = useCallback(() => {
    // Clear the telemetry user context on logout
    clearTelemetryUser();

    // For local development, just redirect to login page
    // In production, use Cognito logout to properly end the SSO session
    const isDevelopment =
      window._env_.REACT_APP_ENV === "development" ||
      window._env_.REACT_APP_ENV === "localhost" ||
      window.location.hostname === "localhost";

    if (isDevelopment) {
      // Local redirect (avoids Cognito logout URL configuration issues). Clearing through the
      // token store rather than storage directly is what tells the mounted provider the session
      // is gone — a same-tab write fires no `storage` event.
      appTokenStore.clear();
      navigate("/login");
    } else {
      // Production: the shared provider clears the session and ends it at the provider's
      // end-session endpoint (Discovery, or the Cognito hosted UI when it advertises none).
      void logout();
    }
  }, [navigate, logout]);

  const toggleVersionsModalAction = useCallback(() => {
    dispatch(toggleModal({ type: VERSION_MODAL }));
    setShowDropdown(false);
  }, []);

  const toggleUserModalAction = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      dispatch(toggleModal({ type: USER_MODAL }));
      setShowDropdown(false);
    },
    []
  );

  // const toggleChangeCompanyModalAction = useCallback(() => {
  //   dispatch(toggleModal({ type: CHANGE_COMPANY_MODAL }));
  //   setShowDropdown(false);
  // }, []);

  const dropdownConfig: Array<DropdownConfigStyles> = [
    // {
    //   id: "change_company",
    //   text: "Change Company",
    //   clickAction: toggleChangeCompanyModalAction,
    //   Icon: ChangeCompanyIcon,
    //   onlyAdmin: true,
    // },
    {
      id: "about",
      text: "About",
      clickAction: toggleVersionsModalAction,
      Icon: AboutIcon,
      onlyAdmin: false,
    },
    // {
    //   id: "django_admin",
    //   text: "Django Admin",
    //   clickAction: () =>
    //     window.open("/api/v1/admin/login/?next=/api/v1/admin/"),
    //   Icon: DjangoAdminIcon,
    //   onlyAdmin: true,
    // },
    {
      id: "logout",
      text: "Logout",
      clickAction: handleLogout,
      Icon: LogOutIcon,
      onlyAdmin: false,
    },
  ];

  return (
    <UserInfoStyled ref={ref}>
      <UserImageStyled onClick={toggleUserModalAction}>
        <UserAvatarComponent />
      </UserImageStyled>
      <UserNameStyled data-test-id="profile" onClick={toggleUserModalAction}>
        {userName}
      </UserNameStyled>
      <UserInfoIconStyled
        data-test-id="menu"
        $active={showDropdown}
        onClick={() => setShowDropdown(!showDropdown)}
      />
      {showDropdown && (
        <DropdownContentStyled>
          {dropdownConfig.map(({ text, clickAction, Icon, onlyAdmin, id }) => {
            const onlyForAdmin = !isAdmin && onlyAdmin;

            if (onlyForAdmin) {
              return null;
            }

            return (
              <DropdownItemStyled
                data-test-id={id}
                key={id}
                onClick={clickAction}
              >
                <Icon />
                {text}
              </DropdownItemStyled>
            );
          })}
        </DropdownContentStyled>
      )}
    </UserInfoStyled>
  );
});
